import DealNotes from '../deals/DealNotes'
import useBreakpoint from '../../hooks/useBreakpoint'

/**
 * Internal staff notes for the quote open on a warehouse screen (Receiving,
 * Testing, Photography, Issue trades, Partially tested). Same notes as the deal
 * modal and Quote Assessment. Any id the screen holds works: the server resolves
 * it to the quote. "Internal" keeps it apart from the customer's own note.
 */
export function QuoteNotesCard({ dealId, style }) {
  return (
    <div className="card" style={{ padding: 'var(--space-4)', ...style }} data-testid="quote-notes">
      <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: 'var(--space-3)' }}>Internal notes</div>
      <DealNotes dealId={dealId} />
    </div>
  )
}

// The notes fill the left gutter between these widths: wide on a wide screen,
// never so narrow that a note wraps every few words.
// Below about 1,500px the 340px floor pulls the content a little right of centre.
const NOTES_WIDTH = 340
const NOTES_MAX_WIDTH = 440
const GAP = 24

/**
 * The open-quote layout. The screen's own content keeps its width and stays
 * centred, exactly where it was without notes; the notes (and any `aside`, such
 * as Receiving's other deals) sit in the empty space to its left. Three grid
 * columns: two equal flexible gutters around the content, the left one never
 * narrower than the notes. Below desktop width the notes stack above instead.
 */
export default function QuoteNotesLayout({ dealId, mainWidth, aside, style, children }) {
  const bp = useBreakpoint()
  // No quote (e.g. a manual listing on Photography): nothing to take notes on.
  if (!dealId) return children

  const notes = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: `${GAP}px` }}>
      <QuoteNotesCard dealId={dealId} />
      {aside}
    </div>
  )

  if (bp !== 'desktop') {
    return (
      <div style={style}>
        <div style={{ maxWidth: `${mainWidth}px`, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: `${GAP}px` }}>
          {notes}
          <div>{children}</div>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `minmax(${NOTES_WIDTH}px, 1fr) minmax(0, ${mainWidth}px) minmax(0, 1fr)`,
      columnGap: `${GAP}px`,
      alignItems: 'start',
      ...style
    }}>
      <div style={{ justifySelf: 'end', width: '100%', maxWidth: `${NOTES_MAX_WIDTH}px` }}>{notes}</div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  )
}
